import React, { useEffect, useState } from 'react';
import styles from '../Translator/Translator.module.scss';
import defaultLanguages from 'data/mock/fileTranslatorLanguages.json';
import { Button } from '@zurich-primitives/button';
import { LanguageSelector, Language } from 'app/components/LanguageSelector';
import { ReactComponent as SwitchIcon } from 'app/assets/Icons/ArrowLeftArrowRight.svg';
import {
  formatTranslatedFileName,
  getFileType,
  handleFileSelection,
  handleFileValidation
} from 'app/utils';
import { useDispatch } from 'react-redux';
import {
  getCurrentFile,
  getService,
  setCurrentFile,
  useAppSelector
} from 'app/redux';
import FileIcon from 'app/assets/Icons/DocumentBlank.svg';
import classnames from 'classnames';
import {
  getTranslationStatus,
  uploadTranslationFile,
  getTranslatedFile,
  getAvailableFileTranslatorLanguages
} from 'integration';
import { LoadingSpinner } from 'app/components';
import { useNavigate } from 'react-router-dom';
import config from 'config';

const DEFAULT_DETECTED_LANGUAGE_LABEL: Language = {
  name: 'Detected language',
  labelName: 'Detected language',
  code: 'autodetect'
};
const DEFAULT_LANGUAGE: Language = {
  name: 'English',
  labelName: 'English',
  code: 'en'
};

const STAGES = {
  UPLOAD: 'UPLOAD',
  TRANSLATE: 'TRANSLATE',
  DOWNLOAD: 'DOWNLOAD'
};
const LENGHT_TEXT_DISPLAY = 35;
type TranslatorJobStatus = 'Running' | 'Succeeded' | 'Error' | 'NotStarted';

export const FileTranslator: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService);
  const currentFile = useAppSelector(getCurrentFile);
  const [currentStage, setCurrentStage] = useState<string>(STAGES.UPLOAD);
  const [errorText, setErrorText] = useState<string | undefined>();
  const [isDragging, setIsDragging] = useState(false);
  const [sourceLanguage, setSourceLanguage] = useState<Language>(
    DEFAULT_DETECTED_LANGUAGE_LABEL
  );
  const [targetLanguage, setTargetLanguage] =
    useState<Language>(DEFAULT_LANGUAGE);
  const [internalFileName, setInternalFileName] = useState<string>('English');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileBlob, setFileBlob] = useState<Blob>();
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>();

  const switchLanguages = (): void => {
    const newOutPutLanguage = sourceLanguage;
    setSourceLanguage(targetLanguage);
    setTargetLanguage(newOutPutLanguage);
  };

  const uploadFile = async (): Promise<string> => {
    const targetLanguageCode = targetLanguage.code;
    const sourceLanguageCode = sourceLanguage.code;

    const requestBody = new FormData();
    requestBody.append('file', currentFile!, currentFile!.name);
    requestBody.append('from', sourceLanguageCode!);
    requestBody.append('to', targetLanguageCode!);

    return await uploadTranslationFile(
      'cross-functional',
      'translator',
      requestBody
    )
      .then((response) => {
        return response.internalName;
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
        throw error;
      });
  };

  const checkTranslationStatus = async (
    internalFileName: string
  ): Promise<TranslatorJobStatus> => {
    return await getTranslationStatus('cross-functional', 'translator')
      .then((response) => {
        const job = response?.statusFiles.find(
          (job: any) => job.internalName === internalFileName
        );
        if (job) {
          const translationStatus = job.status;
          return translationStatus;
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      });
  };

  const downloadFile = async (internalFileName: string): Promise<void> => {
    const requestBody = {
      fileName: internalFileName
    };

    const mimeType = getFileType(internalFileName);

    getTranslatedFile('cross-functional', 'translator', requestBody)
      .then((response) => {
        const blob = new Blob([Uint8Array.from(response.data)], {
          type: mimeType
        });
        setFileBlob(blob);
        triggerDownload(blob, internalFileName);
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const triggerDownload = (blob: Blob, internalFileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const formattedFileName = formatTranslatedFileName(internalFileName);
    link.download = `translated_${formattedFileName}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const translateFile = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const internalName = await uploadFile();
      setInternalFileName(internalName);

      let status: TranslatorJobStatus = await checkTranslationStatus(
        internalName
      );

      while (status !== 'Succeeded') {
        if (status === 'Error') {
          console.error('Translation failed with status "Error".');
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 5000));
        status = await checkTranslationStatus(internalName);
      }

      await downloadFile(internalName);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleTranslate = (): void => {
    translateFile();
    setCurrentStage(STAGES.DOWNLOAD);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];

    if (file !== undefined) {
      const validation = handleFileValidation(file, currentService);
      if (validation.valid) {
        dispatch(setCurrentFile(file));
        setCurrentStage(STAGES.TRANSLATE);
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        setErrorText(errorMessage);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setErrorText(undefined);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSelectionClick = (e: { stopPropagation: () => void }): void => {
    e.stopPropagation();
    setErrorText(undefined);
    handleFileSelection(handleFileChange, currentService);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    const selectedFile: File | undefined = files?.[0];

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        dispatch(setCurrentFile(files?.[0]));
        setCurrentStage(STAGES.TRANSLATE);
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        setErrorText(errorMessage);
      }
    }
  };

  const handleDeleteFile = (): void => {
    dispatch(setCurrentFile(undefined));
    setCurrentStage(STAGES.UPLOAD);
  };

  const handleDownloadClick = async (): Promise<void> => {
    if (internalFileName) {
      if (fileBlob) {
        triggerDownload(fileBlob, internalFileName);
      } else {
        await downloadFile(internalFileName);
      }
    }
  };

  const renderUploadSection = (): React.JSX.Element => (
    <section
      className={classnames(styles['upload-section'], {
        [styles.dragging]: isDragging
      })}
      aria-label="File upload area"
      onDrop={handleFileDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <p>Drag your file here to translate it, or</p>
      <Button onClick={handleSelectionClick} size="small">
        <Button.Label>Select from your computer</Button.Label>
      </Button>
      <p className={styles['supported-files']}>We support .pdf, .txt, .docx.</p>
      <span className={styles.error}>{errorText}</span>
    </section>
  );

  const renderTranslateSection = (fileName: string): React.JSX.Element => (
    <div className={styles['translate-section']}>
      <span className={styles['file-section']}>
        <img src={FileIcon} alt="" aria-hidden />

        <p className={styles['file-name']}>
          {fileName.length > LENGHT_TEXT_DISPLAY
            ? fileName.slice(0, LENGHT_TEXT_DISPLAY) + '...'
            : fileName}
          {fileName.length > LENGHT_TEXT_DISPLAY && (
            <span className={styles.tooltip}>{fileName}</span>
          )}
        </p>
      </span>

      <div className={styles.buttons}>
        <Button size="small" variant="secondary" onClick={handleDeleteFile}>
          <Button.Label>Delete</Button.Label>
        </Button>

        <Button size="small" onClick={handleTranslate}>
          <Button.Label>Translate</Button.Label>
        </Button>
      </div>
    </div>
  );

  const renderDownloadSection = (): React.JSX.Element => {
    return (
      <div className={styles['download-section']}>
        <p>Translation downloaded</p>
        <Button size="small" variant="secondary" onClick={handleDownloadClick}>
          <Button.Label>Download again</Button.Label>
        </Button>
      </div>
    );
  };

  const handleSourceLanguageSelectorChange = (e: {
    target: { value: any };
  }): void => {
    const language = e.target.value;
    setSourceLanguage(language);
  };

  const handleTargetLanguageSelectorChange = (e: {
    target: { value: any };
  }): void => {
    const language = e.target.value;
    setTargetLanguage(language);
  };

  useEffect(() => {
    getAvailableFileTranslatorLanguages('cross-functional', 'translator')
      .then((response) => {
        const sortedLanguages = response.sort((a: Language, b: Language) =>
          a.name.localeCompare(b.name)
        );
        setAvailableLanguages(sortedLanguages);
      })
      .catch((error) => {
        console.error('Error getting available languages:', error);
      });
  }, []);

  const mapLanguages = (
    languages: any[]
  ): Array<{ name: string; labelName: string; code: string }> => {
    return languages?.map((language) => ({
      name: language.name,
      labelName: language.name,
      code: language.code
    }));
  };

  return (
    <div className={styles.translator}>
      <div className={styles['translator-container']}>
        <div className={styles.header}>
          <div className={styles['detected-language']}>
            <LanguageSelector
              languages={[
                DEFAULT_DETECTED_LANGUAGE_LABEL,
                ...(availableLanguages
                  ? mapLanguages(availableLanguages)
                  : mapLanguages(defaultLanguages))
              ]}
              selectedLanguage={sourceLanguage.name}
              handleOnLanguageSelectorChange={
                handleSourceLanguageSelectorChange
              }
              placement="left"
            />
          </div>
          <Button
            variant="tertiary"
            aria-label="Speak"
            disabled={sourceLanguage === DEFAULT_DETECTED_LANGUAGE_LABEL}
            onClick={switchLanguages}
          >
            <Button.Icon>
              <SwitchIcon />
            </Button.Icon>
          </Button>
          <LanguageSelector
            languages={
              availableLanguages
                ? mapLanguages(availableLanguages)
                : mapLanguages(defaultLanguages)
            }
            selectedLanguage={targetLanguage.name}
            handleOnLanguageSelectorChange={handleTargetLanguageSelectorChange}
          />
        </div>
        <div className={styles['file-translator-container']}>
          {currentStage === 'UPLOAD' && renderUploadSection()}
          {currentFile &&
            currentStage === 'TRANSLATE' &&
            renderTranslateSection(currentFile.name)}

          {currentStage === 'DOWNLOAD' && (
            <div className={styles['download-section-container']}>
              {renderUploadSection()}
              {renderDownloadSection()}
            </div>
          )}
        </div>
      </div>

      {isLoading && <LoadingSpinner />}
    </div>
  );
};
